import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { connect } from "react-redux";
import TimecodeDisplay from "./TimecodeDisplay";
import Timecode from "../../util/timecode";
import LuciPropTypes from "../../propTypes/";
import ChannelSelect from "./ChannelSelect";
import PrimeStyleSelect from "./PrimeStyleSelect";
import LayerSelect from "./LayerSelect";
import { TextArea, InputLabel } from "../../styled-components/";
import { actions as assetActions } from "../../actions/asset";
import { makeGetConfigValue } from "../../selectors/config";
import localize from "../../util/localizeUtils";

const Mousetrap = window.Mousetrap;

const Cell = styled.td`
  padding: 0.1rem 0.5rem;
  text-align: left;
  vertical-align: top;
`;

const LayerLabel = styled(InputLabel)`
  font-size: 10px;
  font-weight: bold;
  line-height: 1.3;
`;

class AssetProperties extends React.Component {
  constructor() {
    super();
    this.state = {
      timecodeIn: "00:00:00:00",
      timecodeDuration: "00:00:00:00",
      selectedChannel: "",
      mosAbstract: "",
      layer: ""
    };
  }

  componentDidMount = () => {
    this.bindShortcuts();
    this.updateState(this.props);
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.updateState(nextProps);
  }

  componentWillUnmount = () => {
    this.unbindShortcuts();
  };

  updateState = props => {
    this.setState({
      timecodeIn: props.timecodeIn,
      timecodeDuration: props.timecodeDuration,
      selectedChannel: props.defaultChannel,
      mosAbstract: props.mosAbstract,
      layer: props.layer
    });
    this.updateValue("layer", props.layer);
  };

  bindShortcuts = () => {
    const { openChannelCombo, focusTimecode, focusSlug } = this.props.shortcuts;
    Mousetrap.bindGlobal(openChannelCombo, () => {
      if (this.channelSelectInput) {
        this.channelSelectInput.focus();
      }
    });
    Mousetrap.bindGlobal(focusTimecode, () => {
      if (this.timeCodeInInput) {
        this.timeCodeInInput.focus();
      }
    });
    Mousetrap.bindGlobal(focusSlug, () => {
      if (this.slugInput) {
        this.slugInput.focus();
      }
    });
  };

  unbindShortcuts = () => {
    const { openChannelCombo, focusTimecode, focusSlug } = this.props.shortcuts;
    Mousetrap.unbind(openChannelCombo);
    Mousetrap.unbind(focusTimecode);
    Mousetrap.unbind(focusSlug);
  };

  timecodeInChanged = newTimeCode => {
    this.setState({ timecodeIn: newTimeCode });
    this.updateValue("automation start", newTimeCode);
  };

  timecodeDurationChanged = newTimeCode => {
    this.setState({ timecodeDuration: newTimeCode });
    this.updateValue("automation duration", newTimeCode);
  };

  timecodeOutChanged = newTimeCode => {
    const inTime = Timecode.parseTimecode(this.state.timecodeIn);
    const outTime = Timecode.parseTimecode(newTimeCode);
    const duration = outTime.subtract(inTime);
    this.timecodeDurationChanged(duration.getTimecodeString());
  };

  channelChanged = event => {
    this.setState({ selectedChannel: event.target.value });
    this.updateValue(
      "default channel",
      event.target.options[event.target.selectedIndex].text
    );
  };

  layerChanged = newLayerValue => {
    this.setState({ layer: newLayerValue });
    this.updateValue("layer", newLayerValue);
  };

  slugChanged = event => {
    this.setState({ mosAbstract: event.target.value });
  };

  updateValue = (key, value) => {
    this.props.updateTemplateProperty(key, value);
  };

  render() {
    const {
      channelList,
      showTimecode,
      showChannel,
      showSlug,
      frameRate,
      showLayer,
      layerList
    } = this.props;
    const {
      timecodeIn,
      timecodeDuration,
      selectedChannel,
      mosAbstract
    } = this.state;
    const timecodeOut = Timecode.parseTimecode(timecodeIn, frameRate)
      .add(Timecode.parseTimecode(timecodeDuration, frameRate))
      .getTimecodeString();

    let layer = this.props.layer;

    if (this.state.layer !== "") {
      layer = this.state.layer;
    }

    return (
      <tr>
        {showChannel ? (
          <Cell>
            <ChannelSelect
              channelInputRef={r => (this.channelSelectInput = r)}
              channelList={channelList}
              selectedChannel={selectedChannel}
              onChanged={this.channelChanged}
            />
            {showLayer ? (
              <div>
                <LayerLabel>{localize("layerLabel")}</LayerLabel>
                <LayerSelect
                  id="layerSelect"
                  name={layer}
                  layerList={layerList}
                  selectedLayer={layer}
                  onChange={this.layerChanged}
                  width="5rem"
                  value={layer}
                />
              </div>
            ) : (
              ""
            )}
          </Cell>
        ) : (
          ""
        )}
        {showTimecode ? (
          <Cell>
            <TimecodeDisplay
              hourInputRef={r => (this.timeCodeInInput = r)}
              label={localize("inLabel")}
              timecode={timecodeIn}
              timecodeChanged={this.timecodeInChanged}
              frameRate={frameRate}
            />
            <TimecodeDisplay
              label={localize("durLabel")}
              timecode={timecodeDuration}
              timecodeChanged={this.timecodeDurationChanged}
              frameRate={frameRate}
            />
            <TimecodeDisplay
              label={localize("outLabel")}
              timecode={timecodeOut}
              timecodeChanged={this.timecodeOutChanged}
              frameRate={frameRate}
            />
          </Cell>
        ) : (
          ""
        )}
        {showSlug ? (
          <Cell>
            <TextArea
              ref={r => (this.slugInput = r)}
              type="textarea"
              height="3rem"
              width="100%"
              value={mosAbstract}
              onChange={this.slugChanged}
              onBlur={event => {
                this.updateValue("mos abstract", event.target.value);
              }}
            />
          </Cell>
        ) : (
          ""
        )}
      </tr>
    );
  }
}

AssetProperties.defaultProps = {
  selectedAssetId: null
};

AssetProperties.propTypes = {
  channelList: LuciPropTypes.ChannelList.isRequired,
  /* eslint-disable */
  mosAbstract: PropTypes.string.isRequired,
  timecodeIn: PropTypes.string.isRequired,
  timecodeDuration: PropTypes.string.isRequired,
  defaultChannel: PropTypes.string.isRequired,
  selectedAssetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /* eslint-enable */
  updateTemplateProperty: PropTypes.func.isRequired,
  showTimecode: PropTypes.bool.isRequired,
  showSlug: PropTypes.bool.isRequired,
  showChannel: PropTypes.bool.isRequired,
  shortcuts: PropTypes.shape({
    openChannelCombo: PropTypes.arrayOf(PropTypes.string),
    focusTimecode: PropTypes.arrayOf(PropTypes.string),
    focusSlug: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  frameRate: PropTypes.number.isRequired,
  showLayer: PropTypes.bool.isRequired,
  layer: PropTypes.string,
  layerList: PropTypes.arrayOf(PropTypes.string)
};

const mapStateToProps = state => {
  let timecodeIn = "00:00:00:00";
  let timecodeDuration = "00:00:00:00";
  let defaultChannel = "";
  let mosAbstract = "";
  let layer = "";
  let layerList = [];

  if (
    state.asset.templateDefinition &&
    state.asset.templateDefinition.properties
  ) {
    timecodeIn =
      state.asset.templateDefinition.properties["automation start"] ||
      "00:00:00:00";
    timecodeDuration =
      state.asset.templateDefinition.properties["automation duration"] ||
      "00:00:00:00";
    defaultChannel =
      state.asset.templateDefinition.properties["default channel"] || "";
    mosAbstract =
      state.asset.templateDefinition.properties["mos abstract"] || "";
    layer = state.asset.templateDefinition.properties["layer"] || "";
    layerList = state.asset.templateDefinition.layerList || [];
  }

  if (state.asset.asset.layer && state.asset.asset.layer !== "") {
    layer = state.asset.asset.layer;
  }

  const getConfigValue = makeGetConfigValue();
  return {
    channelList: state.asset.channelList,
    mosAbstract,
    timecodeDuration,
    defaultChannel,
    timecodeIn,
    shortcuts: state.keyShortcuts.shortcuts,
    isModifierActive: state.keyShortcuts.isModifierActive,
    selectedAssetId: state.asset.selectedAssetId,
    showTimecode:
      getConfigValue(state, "userConfig", "timecodeDisplay", "show") === "show",
    showSlug:
      getConfigValue(state, "userConfig", "slugDisplay", "show") === "show",
    showChannel:
      getConfigValue(state, "userConfig", "channelDisplay", "show") === "show",
    frameRate: parseInt(
      getConfigValue(state, "luci", "automationTimecodeFramerate", "30"),
      10
    ),
    showLayer: state.asset.asset.fileType === "prime-graphics",
    layer,
    layerList
  };
};

const mapActionCreators = {
  updateTemplateProperty: assetActions.updateTemplateProperty
};

export default connect(mapStateToProps, mapActionCreators)(AssetProperties);



// WEBPACK FOOTER //
// ./app/components/Editor/AssetProperties.js